import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '../dates.ts';
import { Refusal } from './PropertiesPanel.tsx';
import { RecordEdit } from './RecordEdit.ts';
import {
  BLOCKER_REASONS, useOpenBlocker, useRecordApplication, useResolveBlocker, useWithdrawApplication, useWithdrawBlocker,
  type BlockerReason, type CommissionLine,
} from '../api/commissions.ts';

/**
 * One acting order's line in the commission tracker (167, P-24), as the tracker and the client's Legal section show
 * it: its state in the database's own words, each step said in words (never by colour alone), the bond beside it as
 * information, and an open blocker. Whoever may record (PARALEGAL and tiers 1-2) records the APPLICATION or opens a
 * BLOCKER here; the oath and the commission are the order's records (154), the bond the Bonds panel's (162).
 */

const field = 'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 text-base text-neutral-900';
const label = 'block text-sm font-semibold text-neutral-700';
const hint = 'mt-1 text-sm text-neutral-600';
const primary = 'min-h-[44px] rounded-lg bg-neutral-900 px-4 text-sm font-semibold text-white disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed';
const secondary = 'min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold text-neutral-800 hover:bg-neutral-50';

function todayLocal(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date());
}

/** A step in words: done with its date, done with its date not known, or not yet. */
function step(done: boolean, on: string | null, notYet = 'Not yet'): string {
  if (!done) return notYet;
  return on ? `Done ${formatDate(on)}` : 'Done, date not known';
}

export function CommissionLineView({ line, mayRecord, mayResolve, showClient = false, onSaid }: {
  line: CommissionLine; mayRecord: boolean; mayResolve: boolean; showClient?: boolean; onSaid?: (m: string) => void;
}) {
  const [doing, setDoing] = useState<'none' | 'apply' | 'block' | 'resolve' | 'withdraw_application' | 'withdraw_blocker'>('none');
  const done = (m: string | null) => { setDoing('none'); if (m && onSaid) onSaid(m); };
  const red = line.state === 'no_acting_order';
  const open = !line.commissioned;
  const bondStep = line.bond_required === false ? 'Not required'
    : line.bond_required === null && !line.bond_met ? 'Not known whether one is required'
    : step(line.bond_met, line.bond_issued_on);
  return (
    <div>
      {showClient && (
        <p className="font-semibold text-neutral-900">
          <Link to={`/clients/${line.client_id}/legal`} className="link">{line.display_name}</Link>{' '}
          <span className="font-normal text-neutral-600 tabular-nums">{line.client_ref}</span>
        </p>
      )}
      {line.order_words && <p className="text-sm text-neutral-700">{line.order_words}{line.is_temporary ? ' (temporary)' : ''}</p>}
      <p className={red ? 'font-semibold text-critical-edge' : line.state === 'blocked' ? 'font-semibold text-amber-800' : 'font-semibold text-neutral-900'}>
        {line.waiting_words}
      </p>
      {line.blocker_id && (
        <p className="text-sm text-neutral-700">
          {line.blocker_label}, since {line.blocker_opened ? formatDate(line.blocker_opened) : 'a date not known'}: {line.blocker_note}
        </p>
      )}
      {!red && !line.commissioned_by_order && (
        <dl className="mt-1 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
          <div><dt className="text-neutral-600">Oath and designation</dt><dd className="text-neutral-900">{step(line.oath_met, line.oath_on)}</dd></div>
          <div><dt className="text-neutral-600">Bond</dt><dd className="text-neutral-900">{bondStep}</dd></div>
          <div><dt className="text-neutral-600">Application</dt><dd className="text-neutral-900">{step(line.applied_for, line.applied_on)}</dd></div>
        </dl>
      )}
      {line.bond_words && <p className="mt-1 text-sm text-neutral-700">{line.bond_words}</p>}
      {doing === 'none' && (
        <div className="mt-2 flex flex-wrap gap-2">
          {mayRecord && open && !red && !line.applied_for && line.court_order_id && (
            <button type="button" className={secondary} onClick={() => setDoing('apply')}>Record the application</button>
          )}
          {mayRecord && open && !line.blocker_id && (
            <button type="button" className={secondary} onClick={() => setDoing('block')}>Open a blocker</button>
          )}
          {mayResolve && line.blocker_code === 'DOCUMENTS_UNCLEAR' && (
            <button type="button" className={secondary} onClick={() => setDoing('resolve')}>Mark the documents resolved</button>
          )}
          {/* the one Edit menu every record uses (RecordEdit): a withdrawal, entered in error */}
          {line.application_id && (
            <RecordEdit may={mayRecord} className={secondary} choices={[
              { key: 'withdraw_application', label: 'Withdraw the application: recorded in error', onPick: () => setDoing('withdraw_application') },
            ]} />
          )}
          {line.blocker_id && (
            <RecordEdit may={mayRecord} className={secondary} choices={[
              { key: 'withdraw_blocker', label: 'Withdraw the blocker: opened in error', onPick: () => setDoing('withdraw_blocker') },
            ]} />
          )}
        </div>
      )}
      {doing === 'apply' && line.court_order_id && (
        <ApplicationForm line={line} onDone={done} />
      )}
      {doing === 'block' && <BlockerForm line={line} onDone={done} />}
      {doing === 'resolve' && line.blocker_id && (
        <WordsForm title="Mark the documents resolved" what="How were they resolved?" button="Mark resolved"
                   done="Resolved. Its meeting task is complete." useIt={useResolveBlocker}
                   ids={{ clientId: line.client_id, blockerId: line.blocker_id }} onDone={done} />
      )}
      {doing === 'withdraw_application' && line.application_id && (
        <WordsForm title="Withdraw the application" what="Why" button="Withdraw"
                   done="Application withdrawn. It counts for nothing." useIt={useWithdrawApplication}
                   ids={{ clientId: line.client_id, applicationId: line.application_id }} onDone={done} />
      )}
      {doing === 'withdraw_blocker' && line.blocker_id && (
        <WordsForm title="Withdraw the blocker" what="Why" button="Withdraw"
                   done="Blocker withdrawn. Its task, if any, is closed the ordinary way." useIt={useWithdrawBlocker}
                   ids={{ clientId: line.client_id, blockerId: line.blocker_id }} onDone={done} />
      )}
    </div>
  );
}

function ApplicationForm({ line, onDone }: { line: CommissionLine; onDone: (m: string | null) => void }) {
  const [on, setOn] = useState('');
  const record = useRecordApplication();
  const sending = useRef(false);
  const id = `application-${line.court_order_id}`;
  return (
    <form className="mt-2 space-y-3 rounded-lg border border-neutral-300 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (sending.current || !on) return;
            sending.current = true;
            record.mutate({ clientId: line.client_id, orderId: line.court_order_id!, applied_on: on }, {
              onSuccess: () => onDone('Application recorded: waiting for the court.'),
              onError: () => { sending.current = false; },
            });
          }}>
      <h3 className="font-semibold text-neutral-900">Record the commission application</h3>
      <p className={hint}>The day it was applied for. The document can be attached when the file is ready.</p>
      <div>
        <label className={label} htmlFor={`${id}-on`}>Date applied for</label>
        <input id={`${id}-on`} type="date" className={field} value={on} min={line.order_signed ?? undefined} max={todayLocal()}
               onChange={(e) => setOn(e.target.value)} />
      </div>
      {record.error && <Refusal error={record.error} />}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={!on || record.isPending}>{record.isPending ? 'Recording…' : 'Record'}</button>
        <button type="button" className={secondary} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </form>
  );
}

function BlockerForm({ line, onDone }: { line: CommissionLine; onDone: (m: string | null) => void }) {
  const [reason, setReason] = useState<BlockerReason | ''>('');
  const [note, setNote] = useState('');
  const open = useOpenBlocker();
  const sending = useRef(false);
  const id = `blocker-${line.court_order_id ?? line.client_id}`;
  const chosen = BLOCKER_REASONS.find((r) => r.code === reason);
  const ready = !!reason && note.trim() !== '' && !(reason === 'NOT_EFILED' && !line.court_order_id);
  return (
    <form className="mt-2 space-y-3 rounded-lg border border-neutral-300 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (sending.current || !ready) return;
            sending.current = true;
            open.mutate({ clientId: line.client_id, orderId: line.court_order_id, reason_code: reason as BlockerReason, note: note.trim() }, {
              onSuccess: () => onDone('Blocker opened.'),
              onError: () => { sending.current = false; },
            });
          }}>
      <h3 className="font-semibold text-neutral-900">Open a blocker</h3>
      <div>
        <label className={label} htmlFor={`${id}-reason`}>What is it waiting on?</label>
        <select id={`${id}-reason`} className={field} value={reason} onChange={(e) => setReason(e.target.value as BlockerReason | '')}>
          <option value="">Choose…</option>
          {BLOCKER_REASONS.map((r) => (
            <option key={r.code} value={r.code} disabled={r.code === 'NOT_EFILED' && !line.court_order_id}>{r.label}</option>
          ))}
        </select>
        {chosen && <p className={hint}>{chosen.hint}</p>}
      </div>
      <div>
        <label className={label} htmlFor={`${id}-note`}>Say in words</label>
        <input id={`${id}-note`} className={field} value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} />
      </div>
      {open.error && <Refusal error={open.error} />}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={!ready || open.isPending}>Open the blocker</button>
        <button type="button" className={secondary} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </form>
  );
}

/** A record's act that takes only words: a resolution or a withdrawal. */
function WordsForm<V extends { clientId: string }>({ title, what, button, done, useIt, ids, onDone }: {
  title: string; what: string; button: string; done: string;
  useIt: () => { mutate: (v: V & { reason: string }, o: { onSuccess: () => void }) => void; isPending: boolean; error: Error | null };
  ids: V; onDone: (m: string | null) => void;
}) {
  const [why, setWhy] = useState('');
  const act = useIt();
  const id = `words-${title.replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <form className="mt-2 space-y-3 rounded-lg border border-neutral-300 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!why.trim()) return;
            act.mutate({ ...ids, reason: why.trim() }, { onSuccess: () => onDone(done) });
          }}>
      <h3 className="font-semibold text-neutral-900">{title}</h3>
      <div>
        <label className={label} htmlFor={`${id}-why`}>{what}</label>
        <input id={`${id}-why`} className={field} value={why} maxLength={2000} onChange={(e) => setWhy(e.target.value)} />
      </div>
      {act.error && <Refusal error={act.error} />}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={!why.trim() || act.isPending}>{button}</button>
        <button type="button" className={secondary} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </form>
  );
}
